import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { DemoDomainLog, DemoDomain } from './demoFixtures';

const DEMO_STORAGE_KEY = 'shadowscan_demo_state';

type DemoPolicyTier = DemoDomain['policy_tier'];

type DemoModeState = {
  policyTier: DemoPolicyTier;
  enforcementState: Record<string, boolean>;
  demoEvents: DemoDomainLog[];
};

type DemoModeContextValue = DemoModeState & {
  setPolicyTier: (tier: DemoPolicyTier) => void;
  toggleEnforcement: (controlKey: string, siteId: string, domain: string, enabled: boolean) => void;
  clearDemoEvents: () => void;
};

const DemoModeContext = createContext<DemoModeContextValue | undefined>(undefined);

const defaultState: DemoModeState = {
  policyTier: 'Monitor',
  enforcementState: {},
  demoEvents: [],
};

const loadState = (): DemoModeState => {
  if (typeof window === 'undefined') return defaultState;
  const stored = window.sessionStorage.getItem(DEMO_STORAGE_KEY);
  if (!stored) return defaultState;
  try {
    return { ...defaultState, ...(JSON.parse(stored) as DemoModeState) };
  } catch {
    return defaultState;
  }
};

export const DemoModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<DemoModeState>(() => loadState());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const value = useMemo<DemoModeContextValue>(() => ({
    ...state,
    setPolicyTier: (tier) => setState((prev) => ({ ...prev, policyTier: tier })),
    toggleEnforcement: (controlKey, siteId, domain, enabled) => {
      setState((prev) => ({
        ...prev,
        enforcementState: { ...prev.enforcementState, [controlKey]: enabled },
        demoEvents: [
          {
            id: `${siteId}-${controlKey}-${Date.now()}`,
            site_id: siteId,
            domain,
            created_at: new Date().toISOString(),
            category: 'owasp',
            event_type: 'demo_enforcement_changed',
            severity: 'info',
            summary: `Demo enforcement toggled for ${controlKey} (${enabled ? 'enabled' : 'disabled'}).`,
            control_key: controlKey,
            status: enabled ? 'ok' : 'resolved',
            enforced: enabled,
          },
          ...prev.demoEvents,
        ],
      }));
    },
    clearDemoEvents: () => setState((prev) => ({ ...prev, demoEvents: [] })),
  }), [state]);

  return <DemoModeContext.Provider value={value}>{children}</DemoModeContext.Provider>;
};

export const useDemoMode = () => {
  const context = useContext(DemoModeContext);
  if (!context) {
    throw new Error('useDemoMode must be used within DemoModeProvider');
  }
  return context;
};
